import type { IService } from "../service-registry";
import type {
  CreateNotificationData,
  Notification,
  NotificationsResponse,
} from "../../types/notification";
import { notificationCRUD } from "./notification-crud.service";
import { notificationReader } from "./notification-reader.service";
import { notificationState } from "./notification-state.service";
import type { NotificationServiceResponse } from "./base-notification.service";
import { logger } from "../../lib/logger";

/**
 * Notification Manager Interface
 * Public API for all notification operations
 * Maintains backward compatibility with original implementation
 */
export interface INotificationManager extends IService {
  createNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<Notification>>;
  getNotifications(
    userId: string,
    options?: { limit?: number; includeRead?: boolean; offset?: number }
  ): Promise<NotificationServiceResponse<NotificationsResponse>>;
  markAsRead(notificationIds: string[]): Promise<NotificationServiceResponse<number>>;
  markAllAsRead(userId: string): Promise<NotificationServiceResponse<number>>;
  deleteNotification(notificationId: string): Promise<NotificationServiceResponse<boolean>>;
  deleteReadNotifications(userId: string): Promise<NotificationServiceResponse<number>>;
  deleteOldReadNotifications(
    userId: string,
    beforeDate?: Date
  ): Promise<NotificationServiceResponse<number>>;
  getUnreadCount(userId: string): Promise<NotificationServiceResponse<number>>;
  sendNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<{ success: boolean }>>;
}

/**
 * Notification Manager Facade
 * Coordinates between specialized notification services
 */
class NotificationManager implements INotificationManager {
  readonly name = "notification" as const;

  async initialize(): Promise<void> {
    await notificationCRUD.initialize();
    await notificationReader.initialize();
    await notificationState.initialize();
  }

  async dispose(): Promise<void> {
    await notificationCRUD.dispose();
    await notificationReader.dispose();
    await notificationState.dispose();
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`). Retained
   * for server-side and integration callers.
   */
  async createNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<Notification>> {
    return notificationCRUD.createNotification(data);
  }

  async getNotifications(
    userId: string,
    options?: { limit?: number; includeRead?: boolean; offset?: number }
  ): Promise<NotificationServiceResponse<NotificationsResponse>> {
    return notificationReader.getNotifications(userId, options);
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`). Retained
   * for server-side and integration callers.
   */
  async markAsRead(
    notificationIds: string[]
  ): Promise<NotificationServiceResponse<number>> {
    return notificationState.markAsRead(notificationIds);
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`) or the
   * `useMarkAllRead` hook's server function. Retained for server-side and
   * integration callers.
   */
  async markAllAsRead(userId: string): Promise<NotificationServiceResponse<number>> {
    return notificationState.markAllAsRead(userId);
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`). Retained
   * for server-side and integration callers.
   */
  async deleteNotification(
    notificationId: string
  ): Promise<NotificationServiceResponse<boolean>> {
    return notificationCRUD.deleteNotification(notificationId);
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`). Retained
   * for server-side and integration callers.
   */
  async deleteReadNotifications(
    userId: string
  ): Promise<NotificationServiceResponse<number>> {
    return notificationCRUD.deleteReadNotifications(userId);
  }

  /**
   * @deprecated Browser writes to `notifications` must go through the
   * TanStack DB collection path (`getNotificationsCollection`). Retained
   * for server-side and integration callers.
   */
  async deleteOldReadNotifications(
    userId: string,
    beforeDate?: Date
  ): Promise<NotificationServiceResponse<number>> {
    return notificationCRUD.deleteOldReadNotifications(userId, beforeDate);
  }

  async getUnreadCount(userId: string): Promise<NotificationServiceResponse<number>> {
    return notificationReader.getUnreadCount(userId);
  }

  async sendNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<{ success: boolean }>> {
    // Stub: future implementation will integrate with NotificationSender service
    // for email, SMS, and push notification delivery
    logger.info(`[NotificationManager] sendNotification stub called for user ${data.user_id}`);
    return { data: { success: true }, error: null };
  }
}

export const notificationManager = new NotificationManager();
export { NotificationManager };
